import axios from 'axios';
import Cache from '../controls/Cache'

export const config = {
    buildVersion: process.env.REACT_APP_VERSION,
}

export const helper = {
    makeError: (title, msg, type) => {
        var status = 400;

        if (type === 'warn_only') {
            status = 200;
        } else if (type === 'invalid_client') {
            status = 401;
        }

        return {
            response: {
                data: {
                    error: type,
                    error_title: title,
                    error_description: msg,
                },
                status: status,
            }
        }
    },
    getStackTrace: (e = null) => {
        var stack;

        if (e !== null) {
            stack = e.stack || '';

            if (stack !== '') {
                stack = stack.split('\n');
                return stack;
            }
        }

        try {
            throw new Error('');
        }
        catch (error) {
            stack = error.stack || '';
        }

        stack = stack.split('\n').map(function (line) { return line.trim(); });
        return stack.splice(stack[0] === 'Error' ? 2 : 1);
    },
    sanitizeError: (error, title, msg, type) => {
        const baseErrMsg = error.name + ': ' + error.message;

        if (!error.response) {
            if (error.request) {
                error.response = {
                    data: {
                        error_title: 'Error',
                        error: 'no_response',
                        error_description: 'Unable to get response from server',
                        error_full_description: baseErrMsg,
                    }
                };

                return error;
            } else {
                error.response = {
                    data: {
                        error_title: 'Error',
                        error: 'no_request',
                        error_description: 'Unable to sent request to server',
                        error_full_description: baseErrMsg,
                    }
                };

                return error;
            }
        }

        if (error.response.status === 404) {
            error.response.data = null;
        }

        if (typeof (error.response.data) === 'string') {
            error.response.data = {
                error_description: 'Expired and stale data. Please refresh',
                error_full_description: baseErrMsg + ' - ' + error.response.data,
            };
        } else if (!error.response.data || !error.response.data.errorMessage) {
            error.response.data = {
                error_description: msg,
                error_full_description: baseErrMsg,
            };
        } else {
            if (!msg) {
                msg = 'Invalid server response';
            }

            error.response.data.error_description = msg;
            error.response.data.error_full_description = baseErrMsg + ' - ' + error.response.data.errorMessage;
        }

        if (type) {
            error.response.data.error = type;
        } else if (error.response.status === 401) {
            error.response.data.error = 'invalid_client';
        }

        if (!error.response.data.error) {
            error.response.data.error = 'generic';
        }

        error.response.data.error_title = title;

        return error;
    },
    makeDate: (dateValue) => {
        if (!dateValue) {
            dateValue = 20180301;
        }
        var datevValue_length = dateValue.toString().length
        var dateValue_int = Number(dateValue)

        if (datevValue_length > 6) {

            const year = (dateValue_int / 10000 >> 0);

            var temp = (year * 10000);

            var mth = ((dateValue_int - temp) / 100 >> 0);
            temp = temp + (mth * 100);

            const date = (dateValue_int - temp >> 0);

            const dateObject = new Date(year, mth - 1, date, 0, 0, 0, 0);

            const converted_dateObject = dateObject.toLocaleDateString()

            return converted_dateObject;

        } else {

            const year = (dateValue_int / 100 >> 0);

            var temp = (year * 100);

            var mth = ((dateValue_int - temp) >> 0);

            const dateObject = new Date(year, mth, 0, 0, 0, 0, 0);

            const converted_dateObject = dateObject.toLocaleDateString('en-GB', { year: 'numeric', month: 'short' })

            return converted_dateObject;
        }
    },
    sanitizeDict: (queryString, ...keys) => {
        for (const key of keys) {
            if (!queryString[key]) {
                queryString[key] = '';
            }
        }

        return queryString;
    }
}

export const deferToken = {
    source: () => {
        return {
            cancel: () => { },
        }
    }
}

export const defer = (delay = 0, token = null) => {
    var timerId = 0

    const p = new Promise((resolve, reject) => {
        timerId = setTimeout(resolve, delay);
    });

    if (token) {
        token.cancel = () => {
            clearTimeout(timerId);
        }
    }

    return p;
}

export const redirect = (path, delay = 0, token = null) => {
    return defer(delay, token).then(() => {
        window.location.href = path;
    });
}

export const route = (context, path, search, state = {}, delay = 0, token = null) => {
    return defer(delay, token).then(() => {
        context.props.history.push({
            pathname: url.base(context) + path,
            search: search,
            state: state,
        });
    });
}

export const url = {
    base: (context) => {
        var path = '';

        if (context.props.match.params.base) {
            path = '/' + context.props.match.params.base;
        }

        return path;
    },
    home: () => {
        return process.env.REACT_APP_HOME_REDIRECT_URL;
    },
    login: (state = {}, user = '') => {
        const encodedState = btoa(JSON.stringify(state));

        const redirect_uri = window.location.origin + '/access';

        const core_host_url = process.env.REACT_APP_CORE_HOST_URL;

        return core_host_url + '/login?redirect_uri=' + redirect_uri +
            '&state=' + encodedState + '&user=' + user;
    }
}

const makeApi = (path, port = 7000) => {
    if (window.location.host === 'localhost:3000') {
        return 'http://localhost:' + port + '/api/public/v1';
    }

    return process.env.REACT_APP_AGRI_API + path + '/public';
}

export const api = {
    access: () => {
        return makeApi('/access');
    },
    organization: () => {
        return makeApi('/organization');
    },
    permission: () => {
        return makeApi('/permission');
    },
    map: () => {
        return makeApi('/map');
    },
    tasking: () => {
        return makeApi('/tasking');
    },
    profile: () => {
        return makeApi('/profile');
    },
    privilege: () => {
        return makeApi('/privilege');
    },
    report: () => {
        return makeApi('/report');
    },
    invite: () => {
        return makeApi('/invite');
    },
    diagnostic: () => {
        return makeApi('/diagnostic');
    },
}

export const http = {
    request: async (config) => {
        config.attempts = 3;
        config.attemptFallbackMS = 3000;

        if (config.attempts === undefined) {
            config.attempts = 1;
        }

        if (config.cacheKey) {
            const resp = Cache.query('', config.cacheKey);

            if (resp) {
                return resp;
            }
        }

        while (config.attempts > 0) {
            config.attempts = config.attempts - 1;

            try {
                const resp = await axios.request(config);
                if (config.cacheKey) {
                    if (config.cacheTTL) {
                        Cache.store('', config.cacheKey, resp, config.cacheTTL);
                    } else {
                        Cache.store('', config.cacheKey, resp);
                    }
                }
                return resp;
            } catch (error) {
                if (axios.isCancel(error)) {
                    throw error;
                }

                if (config.attempts === 0) {
                    throw error;
                }

                if (error.response) {
                    const statusCode = error.response.status;
                    const msg = error.response.data.message;

                    if ((statusCode === 429) ||
                        (statusCode === 500 && msg === 'Internal server error')) {

                        await defer(config.attemptFallbackMS);
                        config.attemptFallbackMS = config.attemptFallbackMS + config.attemptFallbackMS;
                    } else {
                        throw error
                    }
                } else {
                    await defer(config.attemptFallbackMS);
                    config.attemptFallbackMS = config.attemptFallbackMS + config.attemptFallbackMS;
                }
            }
        }

        return null;
    },
    post: (url, data, config = {}) => {
        config.method = 'post';
        config.url = url;
        config.data = data;

        return http.request(config);
    },
    get: (url, config = {}) => {
        config.method = 'get';
        config.url = url;

        return http.request(config);
    },
    delete: (url, config = {}) => {
        config.method = 'delete';
        config.url = url;

        return http.request(config);
    },
    isCancel: axios.isCancel,
    all: axios.all,
    CancelToken: axios.CancelToken,
}

export const diagnostic = {
    errorDump: (code, e) => {
        try {
            var tag = '';
            var message = [];
            var trace = helper.getStackTrace(e);
            const verStr = 'version: ' + config.buildVersion;

            if (e.response && e.response.data) {
                tag = e.response.data.error;
                message = [verStr, e.response.data.error_description, e.response.data.error_full_description];
            } else {
                tag = 'std_js_error';
                message = [verStr, e.name + ': ' + e.message];
            }

            http.post(api.diagnostic() + '/error-dump', {
                'access_code': code,
                'tag': tag,
                'message': message,
                'trace': trace
            });
        } catch (error) {
            console.log(error);
        }
    }
}
